﻿import * as React from 'react';
import { RoomTag } from '../view/ImageProcessingRoomTagging';
import { SelectChangeEvent } from '@mui/material/Select';
import { useSaveRoomTag } from './useSaveRoomTag';
import { useDeleteRoomTag } from './useDeleteRoomTag';
import { useTelemetryContext } from '../../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../../telemetry/utils';
import { AuthenticatedUser } from '../../../../../../shared/types';

type UserRoomTagsArgs = {
    user: AuthenticatedUser;
    availableRoomCategories: RoomTag[];
    existingRoomCategories: RoomTag[];
    imageId: number;
    orderId: number;
};

type RoomTagStateUtils = {
    roomTags: string[];
    openAddErrorSnackbar: boolean;
    handleCloseAddErrorSnackbar: () => void;
    openDeleteErrorSnackbar: boolean;
    handleCloseDeleteErrorSnackbar: () => void;
    handleAddRoomTags: (event: SelectChangeEvent) => void;
    handleDeleteRoomTag: (roomTag: string) => void;
};

function transformRoomTagsForSelect(roomCategory: RoomTag): string {
    return roomCategory.value;
}

export function useRoomTags({
    user,
    availableRoomCategories,
    existingRoomCategories,
    imageId,
    orderId
}: UserRoomTagsArgs): RoomTagStateUtils {
    let [roomTags, setRoomTags] = React.useState<string[]>(
        existingRoomCategories.map(transformRoomTagsForSelect)
    );

    let { logger } = useTelemetryContext();

    React.useEffect(() => {
        setRoomTags(existingRoomCategories.map(transformRoomTagsForSelect));
    }, [existingRoomCategories]);

    let [openAddErrorSnackbar, setOpenAddErrorSnackbar] = React.useState<boolean>(false);

    function handleCloseAddErrorSnackbar() {
        setOpenAddErrorSnackbar(false);
    }

    let saveRoomTags = useSaveRoomTag(orderId, imageId, function handleError(error: unknown) {
        setRoomTags(existingRoomCategories.map(transformRoomTagsForSelect));
        
        setOpenAddErrorSnackbar(true);

        let additionalInformation = {
            origin: `ImageProcessingPage.OnAddRoomTag Image: ${imageId} OrderId: ${orderId}`,
            userInfo: getUserInfo(user)
        };

        logger.logException(error, additionalInformation);
    });
    let handleAddRoomTags = (event: SelectChangeEvent) => {
        const {
            target: { value }
        } = event;

        let selectedRoomTags = value.map((val) =>
            availableRoomCategories.find((roomTag) => roomTag.value === val)
        );

        saveRoomTags(selectedRoomTags)
            .then(() => {
                setRoomTags(value);
            })
            .catch((error) => {
                let additionalInformation = {
                    origin: `ImageProcessingPage.OnAddRoomTag Image: ${imageId} OrderId: ${orderId}`,
                    userInfo: getUserInfo(user)
                };

                logger.logException(error, additionalInformation);
            });
    };

    let [openDeleteErrorSnackbar, setOpenDeleteErrorSnackbar] = React.useState<boolean>(false);

    function handleCloseDeleteErrorSnackbar() {
        setOpenDeleteErrorSnackbar(false);
    }

    let deleteRoomTag = useDeleteRoomTag(
        orderId,
        imageId,
        function handleError(error: unknown, roomTag: string) {
            setRoomTags((roomTags) => roomTags.concat([roomTag]));
            setOpenDeleteErrorSnackbar(true);

            let additionalInformation = {
                origin: `ImageProcessingPage.OnDeleteRoomTag Image: ${imageId} OrderId: ${orderId}`,
                userInfo: getUserInfo(user)
            };
            logger.logException(error, additionalInformation);
        }
    );

    let handleDeleteRoomTag = (roomTag: string) => {
        setRoomTags((roomTags) => roomTags.filter((tag) => tag !== roomTag));

        deleteRoomTag(Number(roomTag)).catch((error) => {
            let additionalInformation = {
                origin: `ImageProcessingPage.OnAddRoomTag Image: ${imageId} OrderId: ${orderId}`,
                userInfo: getUserInfo(user)
            };

            logger.logException(error, additionalInformation);

            setRoomTags((roomTags) => roomTags.concat([roomTag]));
        });
    };

    return {
        roomTags,
        handleAddRoomTags,
        handleDeleteRoomTag,
        openAddErrorSnackbar,
        handleCloseAddErrorSnackbar,
        openDeleteErrorSnackbar,
        handleCloseDeleteErrorSnackbar
    };
}
